import React, { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { formatDate } from '@the-platform-group/formatters/dateFormat'
import {
  Grid,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
} from '@the-platform-group/component-library'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { Button } from '@the-platform-group/component-library/Button'

import { getUserIdentities, createAuth0Identity } from 'services/users'
import { useNotification } from 'components/Notification'

const IDENTITIES_COLUMNS = [
  {
    field: 'type',
    width: 100,
  },
  {
    field: 'status',
    width: 100,
    renderCell: ({ value }) => <StatusBadge status={value} />,
  },
  {
    field: 'createdAt',
    headerName: 'Created Date',
    width: 150,
    valueGetter: ({ value }) => formatDate(value, 'MMM dd, yyyy'),
  },
]

const UserIdentities = ({ user }) => {
  const { createNotification } = useNotification()

  const { data: identities, isLoading, error } = useQuery(
    ['userIdentities', user.id],
    () => getUserIdentities(user.id),
    {
      enabled: !!user.id,
      select: data => data || [],
      initialData: [],
    },
  )

  useEffect(() => {
    if (error) {
      let errorMessage
      switch (error.response.status) {
        case 403:
          errorMessage = 'You do not have permission to view user identities'
          break
        case 404:
          errorMessage = 'User not found'
          break
        default:
          errorMessage = 'Failed to load user identities'
          break
      }

      createNotification({
        message: errorMessage,
        variant: 'error',
      })
    }
  }, [error, createNotification])

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" component="h2">
          Identities
        </Typography>
        <AddAuth0IdentityButton userId={user.id} identities={identities} />
      </Grid>

      <DataGrid
        name="UserIdentitiesTable"
        columns={IDENTITIES_COLUMNS}
        commonColumnProps={{ headerAlign: 'left' }}
        rows={identities}
        rowCount={identities.length}
        loading={isLoading}
        localeText={{
          noRowsLabel: 'No identities found for this user.',
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
        autoHeight
      />
    </>
  )
}

// Add Auth0 Identity Button Component
const AddAuth0IdentityButton = ({ userId, identities }) => {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  // Check if user already has Auth0 identity
  const hasAuth0Identity = identities.some(identity => identity.type === 'auth0')

  const { mutate: createIdentity, isLoading } = useMutation({
    mutationFn: () => createAuth0Identity(userId),
    onSuccess: () => {
      createNotification({
        message: 'Auth0 identity created successfully. Invitation email sent.',
        variant: 'success',
      })
      queryClient.invalidateQueries(['userIdentities', userId])
    },
    onError: error => {
      let errorMessage
      switch (error?.response?.status) {
        case 409:
          errorMessage = 'User already has an Auth0 identity'
          break
        case 400:
          errorMessage = 'Failed to create Auth0 identity'
          break
        default:
          errorMessage = 'Failed to create Auth0 identity. Please try again.'
          break
      }

      createNotification({
        message: errorMessage,
        variant: 'error',
      })
    },
  })

  return (
    <Tooltip
      title={
        hasAuth0Identity ? 'User already has Auth0 identity' : 'Create Auth0 identity for this user'
      }
      placement="top"
    >
      <span>
        <Button
          variant="contained"
          size="small"
          onClick={createIdentity}
          disabled={hasAuth0Identity || isLoading}
          sx={{
            minWidth: 140,
            bgcolor: hasAuth0Identity ? 'secondary.main' : 'primary.main',
            '&:hover': {
              bgcolor: hasAuth0Identity ? 'secondary.main' : 'primary.main',
            },
          }}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Add Auth0 Identity'}
        </Button>
      </span>
    </Tooltip>
  )
}

// Status Badge Component
const StatusBadge = ({ status }) => {
  const isActive = status === 'active'

  return (
    <Chip
      label={status === 'active' ? 'Active' : 'Locked'}
      size="small"
      color={isActive ? 'success' : 'error'}
    />
  )
}

export default UserIdentities
